<template>
    <nav class="navbar">
        <div class="container">
            <div class="navbar-brand">
                <router-link class="navbar-item"
                             active-class="is-active"
                             :to="{ name: 'home' }"
                             exact>
                    <img src="/static/logos/logo-navbar.png" alt="Taskflex.dk">
                </router-link>

                <div v-if="!isLoggedIn" class="navbar-brand-menu is-hidden-desktop">
                    <router-link class="navbar-item" active-class="is-active" :to="{ name: 'customers.register' }" exact>
                        {{$t('global.actions.register')}}
                    </router-link>

                    <a class="navbar-item">
                        <button class="button is-primary is-outlined" @click="showLoginModal = true">
                            <b-icon icon="login-variant" />&nbsp;<span>{{$t('global.actions.login')}}</span>
                        </button>
                    </a>

                    <b-modal v-if="showLoginModal" :active="true" :canCancel="['escape']" @close="showLoginModal = false" has-modal-card>
                        <div class="modal-card">
                            <header class="modal-card-head">
                                <h3 class="modal-card-title">{{$t('global.actions.login')}}</h3>
                                <button @click="showLoginModal = false" class="delete" :aria-label="$t('global.actions.close')" />
                            </header>

                            <section class="modal-card-body">
                                <quick-user-login :has-notification-icons="false" @close="showLoginModal = false"/>
                            </section>
                        </div>
                    </b-modal>
                </div>

                <div class="navbar-burger burger" @click="toggleMenu" :class="{'is-active': expandedMobile, 'disable-margin': !isLoggedIn}">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
            </div>

            <div id="main-navbar" class="navbar-menu" :class="{'is-active': expandedMobile}" @click="expandedMobile = false">
                <div class="navbar-start">
                    <router-link class="navbar-item" active-class="is-active" :to="{ name: 'home' }" exact>
                        {{$t('pages.home.title')}}
                    </router-link>

                    <router-link class="navbar-item" active-class="is-active" :to="{ name: 'students.search' }" exact>
                        {{$t('pages.students.title')}}
                    </router-link>

                    <div class="navbar-item has-dropdown is-hoverable">
                        <router-link class="navbar-item" active-class="is-active-fake" :to="{ name: 'skills.groups-overview' }" exact>
                            <span>{{$t('pages.groups-overview.title')}}</span>
                            <b-icon icon="menu-down" />
                        </router-link>

                        <div class="navbar-dropdown is-boxed">
                            <router-link v-for="group in skillGroups" :key="group.id" class="navbar-item" active-class="is-active"
                                         :to="{ name: 'skills.group', params: { group: group.name }}" exact>
                                {{$t(`skills.groups.${group.name}.name`)}}
                            </router-link>

                            <hr class="navbar-divider">

                            <router-link class="navbar-item" active-class="is-active" :to="{ name: 'skills.groups-overview' }" exact>
                                {{$t('skills.all-areas')}}
                            </router-link>
                        </div>
                    </div>
                </div>

                <div v-if="!isLoggedIn" class="navbar-end is-hidden-touch">
                    <router-link class="navbar-item" active-class="is-active" :to="{ name: 'contact' }" exact>
                        {{$t(`pages.contact.title`)}}
                    </router-link>

                    <router-link class="navbar-item" active-class="is-active" :to="{ name: 'new-student' }" exact>
                        {{$t(`pages.new-student.headerTitle`)}}
                    </router-link>

                    <router-link class="navbar-item" active-class="is-active" :to="{ name: 'customers.register' }" exact>
                        {{$t('global.actions.register')}}
                    </router-link>

                    <b-dropdown position="is-bottom-left" :mobile-modal="false" ref="loginDropdown">
                        <a class="navbar-item" slot="trigger">
                            <button class="button is-primary is-outlined">
                                <b-icon icon="login-variant" />&nbsp;<span>{{$t('global.actions.login')}}</span>
                            </button>
                        </a>

                        <b-dropdown-item custom paddingless>
                                <div class="modal-card" style="width:300px;">
                                    <section class="modal-card-body">
                                        <quick-user-login :has-notification-icons="false" @close="$refs.loginDropdown.toggle()"/>
                                    </section>
                                </div>
                        </b-dropdown-item>
                    </b-dropdown>
                </div>

                <div v-else class="navbar-end">
                    <router-link class="navbar-item" active-class="is-active" :to="{ name: 'users.overview' }" exact>
                        <b-icon icon="poll-box" />
                        <span>{{$t('pages.overview.title')}}</span>
                    </router-link>

                    <div class="navbar-item has-dropdown is-hoverable">
                        <span class="navbar-link avatar-link">
                            <avatar :account="currentUserAccount" :size="36" :round="true" /> &nbsp;

                            {{ currentUserAccount.name | prettyName }}
                        </span>

                        <div @click="closeDropdown" class="navbar-dropdown is-boxed">
                            <router-link v-if="isStudent" class="navbar-item" active-class="is-active"
                                         :to="{ name: 'students.profile', params: { slug: currentUserAccount.slug }}" exact>
                                <b-icon icon="emoticon-outline" />
                                <span>{{$t('pages.users.profile.title')}}</span>
                            </router-link>

                            <router-link v-if="isEmployee" class="navbar-item" active-class="is-active" :to="{ name: 'administration' }" exact>
                                <b-icon icon="poll-box" />
                                <span>Administration</span>
                            </router-link>

                            <router-link class="navbar-item" active-class="is-active"
                                         :to="{ name: 'users.account' }" exact>
                                <b-icon icon="account" />
                                <span>{{$t('pages.users.account.title')}}</span>
                            </router-link>

                            <hr class="navbar-divider">

                            <a class="navbar-item" @click="logout">
                                <b-icon icon="logout-variant" />
                                <span>{{$t('global.actions.logout')}}</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </nav>
</template>

<style lang="scss" scoped>
    .navbar {
        box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1);

        .disable-margin {
            margin-left: 0;
        }

        .navbar-brand-menu {
            margin-left: auto;
            display: flex;
        }
        .avatar-link {
            > figure {
                display: inline-block;
            }
        }
    }
</style>

<script>
    import Avatar from '@/taskflex/users/components/Avatar';
    import QuickUserLogin from '@/infrastructure/auth/components/QuickUserLogin';
    import { mapGetters } from 'vuex';
    import SkillGroupService from '@/taskflex/skills/services/SkillGroupService';

    export default {
        data() {
            return {
                expandedMobile: false,
                showLoginModal: false,
                skillGroups: [],
            };
        },

        computed: {
            ...mapGetters('auth', [
                'isLoggedIn',
                'isStudent',
                'isEmployee',
                'currentUserAccount',
            ]),
        },

        mounted() {
            this.loadSkillGroups();
        },

        methods: {
            loadSkillGroups() {
                const options = {
                    filter_groups: [
                        {
                            filters: [
                                {
                                    key: 'enabled',
                                    operator: 'eq',
                                    value: 1,
                                },
                            ],
                        },
                    ],
                };

                SkillGroupService.findAll(options, ({ data: { rows } }) => {
                    this.skillGroups = rows;
                });
            },

            logout() {
                this.$store.dispatch('auth/logout');

                this.$router.push({ name: 'home' });
            },

            toggleMenu() {
                this.expandedMobile = !this.expandedMobile;
            },

            closeDropdown(event) {
                const dropdownBox = event.target.parentElement;

                dropdownBox.setAttribute('style', 'opacity: 0; pointer-events: none;');

                setTimeout(() => {
                    dropdownBox.removeAttribute('style');
                }, 100);
            },
        },

        components: {
            Avatar,
            QuickUserLogin,
        },
    };
</script>



// WEBPACK FOOTER //
// src/global/layout/Header.vue